// pkg195 — owner-only Import payments (/admin/accounting/import). Preview and commit use POST /api/admin/payment-import/preview and /commit. The CSV is sent as { csv }. Client choices are mappings { [name in the file]: client_id }. Agreement dates are agreement_dates { [client_id]: 'YYYY-MM-DD' }. Confirm import sends preview_token plus include (the external_ref values still checked under Will import). A new name or date is sent back through preview so the row moves to the group the server assigns — delivered 2026-10-31
import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import {
  IMPORT_BUCKETS,
  buildPreviewBody,
  buildCommitBody,
  groupPreview,
  selectedRefs,
  nextSelection,
  findRow,
  reasonText,
} from '../utils/paymentImport';

const F = { fontFamily: 'Arial, sans-serif' };
const money = (n) => '$' + (Number(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const fmtDate = (s) => {
  if (!s) return '—';
  const d = String(s).slice(0, 10).split('-').map(Number);
  if (d.length !== 3 || !d[2]) return String(s);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d[1] - 1]} ${d[2]}, ${d[0]}`;
};
const th = { ...F, fontSize: 11, fontWeight: 700, color: '#fff', background: '#0F1838', padding: '7px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { ...F, fontSize: 13, padding: '8px 10px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };

function accountLabel(account) {
  if (account === 'operating') return 'Operating';
  if (account === 'trust') return 'Trust';
  if (!account) return '—';
  return String(account);
}

function clientRecords(data) {
  const list = Array.isArray(data) ? data : [];
  return list.map((c) => ({
    id: c.id == null ? '' : String(c.id),
    name: c.name || 'Client',
  })).filter((c) => c.id);
}

function noteFor(row) {
  return reasonText(row.reason) || row.description || '—';
}

export default function AdminPaymentImportPage() {
  const fileRef = useRef(null);
  const [filename, setFilename] = useState('');
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [tab, setTab] = useState('will_import');
  const [selected, setSelected] = useState({});
  const [mappings, setMappings] = useState({});
  const [agreementDates, setAgreementDates] = useState({});
  const [dateDraft, setDateDraft] = useState({});
  const [clients, setClients] = useState([]);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [phase, setPhase] = useState('upload');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const bucket = IMPORT_BUCKETS.find((b) => b.key === tab) || IMPORT_BUCKETS[0];
  const rows = (preview && preview.buckets[tab]) || [];
  const include = useMemo(() => selectedRefs(selected), [selected]);
  const readyTotal = useMemo(() => {
    if (!preview) return 0;
    return preview.buckets.will_import.reduce((sum, row) => {
      if (!row.external_ref || !selected[row.external_ref]) return sum;
      return sum + (Number(row.amount) || 0);
    }, 0);
  }, [preview, selected]);

  function resetAll() {
    setFilename('');
    setCsv('');
    setPreview(null);
    setTab('will_import');
    setSelected({});
    setMappings({});
    setAgreementDates({});
    setDateDraft({});
    setClients([]);
    setClientsLoaded(false);
    setPhase('upload');
    setRefreshing(false);
    setError('');
    setResult(null);
  }

  function applyPreview(data, previousSelected, focusRef) {
    const next = groupPreview(data);
    setPreview(next);
    setSelected(nextSelection(previousSelected, next.buckets.will_import));
    if (focusRef) {
      const moved = findRow(next, focusRef);
      if (moved && moved.bucket) setTab(moved.bucket);
    }
    if ((next.buckets.no_client_match || []).length && !clientsLoaded) {
      adminApi.listClients('all')
        .then((list) => { setClients(clientRecords(list)); setClientsLoaded(true); })
        .catch(() => { setClients([]); setClientsLoaded(true); });
    }
    return next;
  }

  async function loadPreview(text, maps, dates, previousSelected, focusRef) {
    const data = await adminApi.previewPaymentImport(buildPreviewBody({
      csv: text,
      mappings: maps,
      agreement_dates: dates,
    }));
    return applyPreview(data, previousSelected, focusRef);
  }

  async function onFile(file) {
    if (!file) return;
    setError('');
    setResult(null);
    setPreview(null);
    setFilename(file.name);
    setPhase('reading');
    try {
      const text = await file.text();
      if (!String(text || '').trim()) {
        setPhase('upload');
        setError('That file is empty. Choose the CSV of payments from your accounting email log.');
        return;
      }
      setCsv(text);
      setMappings({});
      setAgreementDates({});
      setDateDraft({});
      await loadPreview(text, {}, {}, null, null);
      setTab('will_import');
      setPhase('review');
    } catch (e) {
      setPreview(null);
      setPhase('upload');
      setError(e.message || 'The file could not be read.');
    }
  }

  async function rerun(nextMappings, nextDates, focusRef) {
    setRefreshing(true);
    setError('');
    try {
      await loadPreview(csv, nextMappings, nextDates, selected, focusRef);
    } catch (e) {
      setError(e.message || 'The review could not be updated. Your file is still here.');
    } finally {
      setRefreshing(false);
    }
  }

  function onMap(row, clientId) {
    const name = row.client_name;
    const next = { ...mappings };
    if (clientId) next[name] = clientId;
    else delete next[name];
    setMappings(next);
    rerun(next, agreementDates, row.external_ref);
  }

  function clearMapping(row) {
    const next = { ...mappings };
    delete next[row.client_name];
    setMappings(next);
    rerun(next, agreementDates, row.external_ref);
  }

  function onDate(row, value) {
    setDateDraft((prev) => ({ ...prev, [row.client_id]: value }));
    if (value !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    const next = { ...agreementDates };
    if (value) next[row.client_id] = value;
    else delete next[row.client_id];
    setAgreementDates(next);
    rerun(mappings, next, row.external_ref);
  }

  function clearDate(row) {
    const next = { ...agreementDates };
    delete next[row.client_id];
    setAgreementDates(next);
    setDateDraft((prev) => {
      const copy = { ...prev };
      delete copy[row.client_id];
      return copy;
    });
    rerun(mappings, next, row.external_ref);
  }

  function toggle(ref) {
    setSelected((prev) => ({ ...prev, [ref]: !prev[ref] }));
  }

  function toggleAll(on) {
    setSelected((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (row.external_ref) next[row.external_ref] = on;
      });
      return next;
    });
  }

  const checkable = rows.filter((row) => row.external_ref);
  const tabSelected = checkable.filter((row) => selected[row.external_ref]);
  const allOn = bucket.selectable && checkable.length > 0 && tabSelected.length === checkable.length;
  const someOn = bucket.selectable && tabSelected.length > 0 && !allOn;

  function optionsFor(row) {
    const suggested = [];
    const seen = new Set();
    (row.suggestions || []).forEach((s) => {
      if (!s || s.client_id == null) return;
      const id = String(s.client_id);
      if (seen.has(id)) return;
      seen.add(id);
      suggested.push({ id, name: s.name || 'Client' });
    });
    const rest = clients.filter((c) => !seen.has(c.id));
    return { suggested, rest };
  }

  async function confirm() {
    if (!preview || !include.length || phase === 'saving' || refreshing) return;
    setError('');
    setPhase('saving');
    try {
      const data = await adminApi.commitPaymentImport(buildCommitBody({
        preview_token: preview.preview_token,
        mappings,
        agreement_dates: agreementDates,
        include,
      }));
      setResult(data);
      setPhase('done');
    } catch (e) {
      setPhase('review');
      setError(e.message || 'The import did not save. Nothing new was added.');
    }
  }

  const methodLabel = (preview && preview.method_label) || 'Unknown';
  const account = accountLabel(preview && preview.deposit_account);

  return (
    <div>
      <h1 className="page-title">Import payments</h1>
      <p className="page-subtitle">
        Bring in payments from your accounting email log.
        You will review every row first. Nothing is saved until you press Confirm import.
        Saved payments are recorded as Unknown, in the operating account, and are not applied to an invoice.
      </p>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {phase !== 'done' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title">CSV file</div>
          <p style={{ ...F, fontSize: 13, color: '#555', marginTop: 0 }}>
            Choose the CSV from your accounting email log. The file needs columns for the client, the date, the amount, whether the row is counted, and the email thread id.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files && e.target.files[0];
              if (file) onFile(file);
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="btn btn-primary"
            disabled={phase === 'reading' || phase === 'saving' || refreshing}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            {phase === 'reading' ? 'Reading the file…' : (preview ? 'Choose a different file' : 'Choose CSV')}
          </button>
          {filename && phase !== 'reading' && (
            <span style={{ ...F, fontSize: 13, color: '#555', marginLeft: 12 }}>{filename}</span>
          )}
        </div>
      )}

      {preview && phase !== 'done' && (
        <>
          {preview.refunds && preview.refunds.skipped > 0 && (
            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              {preview.refunds.skipped} refund {preview.refunds.skipped === 1 ? 'row was' : 'rows were'} left out.
              {' '}{preview.refunds.detail}
            </div>
          )}
          {preview.ignored_agreement_dates.length > 0 && (
            <div className="alert alert-info" style={{ marginBottom: 12 }}>
              A date you entered was not a real calendar date, so it was not used.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 14 }}>
            {IMPORT_BUCKETS.map((b) => {
              const active = tab === b.key;
              const count = preview.counts[b.key] == null ? preview.buckets[b.key].length : preview.counts[b.key];
              return (
                <button
                  key={b.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setTab(b.key)}
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    borderRadius: 6,
                    padding: '10px 12px',
                    background: active ? '#F0F2F8' : '#fff',
                    border: active ? '2px solid #0F1838' : '1px solid #e4e0d6',
                  }}
                >
                  <div style={{ ...F, fontSize: 12, fontWeight: 700, color: '#0F1838' }}>{b.label}</div>
                  <div style={{ ...F, fontSize: 20, fontWeight: 700, marginTop: 2 }}>{count}</div>
                  <div style={{ ...F, fontSize: 12, color: '#666' }}>{money(preview.totals[b.key])}</div>
                </button>
              );
            })}
          </div>

          <div className="card">
            <div className="card-title">
              {bucket.label}
              {refreshing ? <span style={{ ...F, fontWeight: 400, fontSize: 13, color: '#888' }}> — Updating…</span> : null}
            </div>
            <p style={{ ...F, fontSize: 13, color: '#555', marginTop: 0 }}>{bucket.hint}</p>
            {rows.length === 0 ? (
              <p style={{ ...F, fontSize: 13, color: '#888', margin: 0 }}>Nothing in this group.</p>
            ) : (
              <div className="table-wrap" style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 36 }}>
                        {bucket.selectable ? (
                          <input
                            type="checkbox"
                            checked={allOn}
                            ref={(el) => { if (el) el.indeterminate = someOn; }}
                            onChange={(e) => toggleAll(e.target.checked)}
                            aria-label={`Select all in ${bucket.label}`}
                            disabled={refreshing}
                          />
                        ) : null}
                      </th>
                      <th style={th}>Date</th>
                      <th style={th}>Client</th>
                      <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                      <th style={th}>Method</th>
                      <th style={th}>Account</th>
                      <th style={th}>Agreement date</th>
                      <th style={th}>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const opts = bucket.key === 'no_client_match' ? optionsFor(row) : null;
                      const mappedId = mappings[row.client_name] || '';
                      const draft = row.client_id && dateDraft[row.client_id] != null
                        ? dateDraft[row.client_id]
                        : (row.client_id && agreementDates[row.client_id]) || '';
                      return (
                        <tr key={`${row.bucket}-${row.row_number}-${row.external_ref || ''}`}>
                          <td style={td}>
                            {bucket.selectable && row.external_ref ? (
                              <input
                                type="checkbox"
                                checked={!!selected[row.external_ref]}
                                onChange={() => toggle(row.external_ref)}
                                aria-label={`Include ${row.client_name || 'this row'}`}
                                disabled={refreshing}
                              />
                            ) : null}
                          </td>
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(row.date)}</td>
                          <td style={td}>
                            {bucket.key === 'no_client_match' ? (
                              <>
                                <select
                                  value={mappedId}
                                  disabled={refreshing}
                                  aria-label={`Client for ${row.client_name || 'this payment'}`}
                                  style={{ maxWidth: 240 }}
                                  onChange={(e) => onMap(row, e.target.value)}
                                >
                                  <option value="">Choose a client</option>
                                  {opts.suggested.length > 0 && (
                                    <optgroup label="Suggested">
                                      {opts.suggested.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </optgroup>
                                  )}
                                  {opts.rest.length > 0 && (
                                    <optgroup label={opts.suggested.length ? 'All other clients' : 'Clients'}>
                                      {opts.rest.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </optgroup>
                                  )}
                                </select>
                                {row.client_name ? (
                                  <div style={{ ...F, fontSize: 11, color: '#888', marginTop: 3 }}>In the file: {row.client_name}</div>
                                ) : null}
                                {opts.suggested.length === 0 && opts.rest.length === 0 ? (
                                  <div style={{ ...F, fontSize: 11, color: '#888', marginTop: 3 }}>
                                    {clientsLoaded ? 'No clients are available to choose.' : 'Loading clients…'}
                                  </div>
                                ) : null}
                              </>
                            ) : (
                              <>
                                <span style={{ fontWeight: 600 }}>{row.matched_name || row.client_name || '—'}</span>
                                {row.match === 'mapping' && row.client_name ? (
                                  <div style={{ ...F, fontSize: 11, color: '#888', marginTop: 3 }}>
                                    In the file: {row.client_name}
                                    {' '}
                                    <button type="button" className="btn btn-sm" disabled={refreshing} onClick={() => clearMapping(row)}>Choose a different client</button>
                                  </div>
                                ) : null}
                              </>
                            )}
                          </td>
                          <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{row.amount == null ? '—' : money(row.amount)}</td>
                          <td style={td}>{methodLabel}</td>
                          <td style={td}>{account}</td>
                          <td style={td}>
                            {bucket.key === 'no_agreement_date' && row.client_id ? (
                              <input
                                type="date"
                                value={draft}
                                disabled={refreshing}
                                aria-label={`Agreement date for ${row.client_name || 'this client'}`}
                                onChange={(e) => onDate(row, e.target.value)}
                              />
                            ) : (
                              <>
                                {fmtDate(row.agreement_date)}
                                {row.agreement_date_source === 'manual' && row.client_id ? (
                                  <div style={{ marginTop: 4 }}>
                                    <button type="button" className="btn btn-sm" disabled={refreshing} onClick={() => clearDate(row)}>Change this date</button>
                                  </div>
                                ) : null}
                              </>
                            )}
                          </td>
                          <td style={{ ...td, color: '#555' }}>{noteFor(row)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card" style={{ marginTop: 16, background: '#F7F5EF' }}>
            <p style={{ ...F, fontSize: 15, margin: '0 0 12px', color: '#0F1838' }}>
              {include.length === 0
                ? 'No payments are ready to save. Rows under Will import are the ones that will be saved. A row that still needs a client or an agreement date is in another group until you fill that in.'
                : `Confirm import will save ${include.length} payment${include.length === 1 ? '' : 's'}, totaling ${money(readyTotal)}, as ${methodLabel} in the ${account.toLowerCase()} account. They will not be applied to an invoice.`}
            </p>
            {preview.expires_at ? (
              <p style={{ ...F, fontSize: 12, color: '#888', marginTop: 0 }}>
                This review is kept until {fmtDate(preview.expires_at)}. After that, choose the file again.
              </p>
            ) : null}
            <button
              type="button"
              className="btn btn-primary"
              disabled={include.length === 0 || phase === 'saving' || refreshing}
              onClick={confirm}
              style={{ fontSize: 18, fontWeight: 700, padding: '14px 28px' }}
            >
              {phase === 'saving' ? 'Saving…' : 'Confirm import'}
            </button>
          </div>
        </>
      )}

      {phase === 'done' && result && (
        <div className="card">
          <div className="card-title">Import finished</div>
          <div className="alert alert-success">
            Saved {result.inserted} payment{result.inserted === 1 ? '' : 's'} as {result.method_label || 'Unknown'}, in the {accountLabel(result.deposit_account).toLowerCase()} account.
            {result.applied_to_invoices === false ? ' They were not applied to an invoice.' : ''}
          </div>
          {result.already_on_file > 0 && (
            <p style={{ ...F, fontSize: 14, color: '#333' }}>
              {result.already_on_file} {result.already_on_file === 1 ? 'was' : 'were'} already on file and {result.already_on_file === 1 ? 'was' : 'were'} not saved again.
            </p>
          )}
          {Array.isArray(result.not_imported) && result.not_imported.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <p style={{ ...F, fontSize: 13, fontWeight: 700, marginBottom: 6 }}>These were not saved:</p>
              <ul style={{ ...F, fontSize: 13, color: '#555', marginTop: 0 }}>
                {result.not_imported.map((item, i) => (
                  <li key={`${item.external_ref || ''}-${item.row_number || i}`}>
                    {item.row_number ? `Row ${item.row_number}: ` : ''}{reasonText(item.reason)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <Link to="/admin/dashboard/payments" className="btn btn-primary">See them on Payments</Link>
            <button type="button" className="btn" onClick={resetAll}>Import another file</button>
          </div>
        </div>
      )}
    </div>
  );
}
